/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Autosuggestions } from '@msdyn365-commerce/commerce-entities';
import MsDyn365, { getUrlSync } from '@msdyn365-commerce/core';
import { ProductRefiner } from '@msdyn365-commerce/retail-proxy';
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { FormComponent, LabelComponent } from './components';
import { IUnbxdSearchData } from './unbxd-search.data';
import { IUnbxdSearchProps } from './unbxd-search.props.autogenerated';

export interface ISearchState {
    isSearchFormExpanded: boolean;
    searchText: string;
    searchKeywordSuggestClass: string;
    searchProductSuggestClass: string;
    searchCategorySuggestClass: string;
    suggestions: Autosuggestions | undefined;
    searchRefiners?: ProductRefiner[] | undefined;
    autoSuggestAriaLabel: string;
    isInitialState: boolean;
    isLoadingAutoSuggest?: boolean;
}

export interface ISearchViewProps extends IUnbxdSearchProps<IUnbxdSearchData> {
    Search: IModuleProps;
    AutoSuggestAriaLabel: INodeProps;
    AutoSuggestAriaLabelText: string;
    searchText: string;
    AutoSuggest: INodeProps;
    // KeywordSuggest: INodeProps;
    // ProductSuggest: INodeProps;
    // CategorySuggest: INodeProps;
    SearchForm: INodeProps;
    FormWrapper: INodeProps;
    // UlKeyword: INodeProps;
    // UlProduct: INodeProps;
    // UlCategory: INodeProps;
    label: React.ReactNode;
    form: React.ReactNode;
    // autosuggestKeyword?: ISearchKeywordViewProps;
    // autosuggestProduct?: ISearchProductViewProps;
    // autosuggestCategory?: ISearchCategoryViewProps;
    // callbacks: {
    //     handleCancelSearchChange(): void;
    // };
    isSearchFormExpanded: boolean;
    // isLoadingAutoSuggest?: boolean;
    // isLoadingNode?: React.ReactNode;
    unbxdAutosuggestJs: string | undefined;
    unbxdAutosuggestCss: string | undefined;
}

/**
 *
 * Search module.
 * @extends {React.Component<IUnbxdSearchProps<IUnbxdSearchData>, ISearchState>}
 */
class Search extends React.Component<IUnbxdSearchProps<IUnbxdSearchData>, ISearchState> {
    private readonly searchTextInput: React.RefObject<HTMLInputElement>;

    private readonly searchCollapsedLabel: React.RefObject<HTMLButtonElement>;

    private readonly formReference: React.RefObject<HTMLButtonElement>;

    private readonly maxChars: number;

    private readonly autoSuggestResultScreenReaderDiv: React.RefObject<HTMLDivElement>;

    private readonly autoSuggestResultDiv: React.RefObject<HTMLDivElement>;

    private readonly searchFormClass: string;

    private readonly searchLabelClass: string;

    private readonly telemetryContent: ITelemetryContent;

    // @ts-expect-error
    private cancellabelDebounce: debounce;

    public constructor(props: IUnbxdSearchProps<IUnbxdSearchData>) {
        super(props);
        this.searchTextInput = React.createRef();
        this.searchCollapsedLabel = React.createRef();
        this.autoSuggestResultScreenReaderDiv = React.createRef();
        this.autoSuggestResultDiv = React.createRef();
        this.formReference = React.createRef();
        this.state = {
            isSearchFormExpanded: false,
            searchText: '',
            searchKeywordSuggestClass: 'ms-search__autoSuggest__keyword',
            searchProductSuggestClass: 'ms-search__autoSuggest__product',
            searchCategorySuggestClass: 'ms-search__autoSuggest__category',
            suggestions: undefined,
            searchRefiners: undefined,
            autoSuggestAriaLabel: '',
            isInitialState: true,
            isLoadingAutoSuggest: false
        };
        this.maxChars =
            (this.props.context &&
                this.props.context.app &&
                this.props.context.app.config &&
                this.props.context.app.config.searchInputMaxLength) ||
            50;

        this.searchFormClass = 'ms-search__form';
        this.searchLabelClass = 'ms-search__label';
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    // eslint-disable-next-line complexity -- ignore complexity.
    public render(): JSX.Element {
        const {
            context,
            resources: { searchtext, searchLabelArialLabel, cancelBtnAriaLabel, searchBtnAriaLabel, submitBtnAriaLabel },
            config: { hideSearchLabel, disableSubmitSearch, searchplaceholderText, className = '', unbxdAutosuggestJs, unbxdAutosuggestCss }
        } = this.props;

        const { isSearchFormExpanded, searchText } = this.state;

        const searchQsp = (context && context.app && context.app.config && context.app.config.searchQueryStringParameter) || 'q';
        const searchPageURL = getUrlSync('search', context && context.actionContext) || '';
        const searchURL = this._appendQueryParams(searchPageURL, searchText, searchQsp);
        const searchPlaceholder = searchplaceholderText && searchplaceholderText.length > 0 ? searchplaceholderText : 'Search in Fabrikam';

        const viewProps: ISearchViewProps = {
            ...(this.props as IUnbxdSearchProps<IUnbxdSearchData>),

            Search: {
                moduleProps: this.props,
                className: `${classnames('ms-search', className)} ${disableSubmitSearch ? 'no-submit' : 'with-submit'}`
            },
            AutoSuggestAriaLabel: {
                tag: 'div',
                className: 'msc-autoSuggest__screen-reader',
                'aria-live': 'assertive',
                ref: this.autoSuggestResultScreenReaderDiv
            },
            AutoSuggestAriaLabelText: this.state.autoSuggestAriaLabel,
            searchText: this.state.searchText,
            AutoSuggest: {
                ref: this.autoSuggestResultDiv,
                className: 'ms-search__autoSuggest'
            },
            SearchForm: {
                className: `${this.searchFormClass} ${isSearchFormExpanded ? 'bx-show' : 'bx-hide'}`
            },
            FormWrapper: {
                tag: 'form',
                ref: this.formReference,
                className: 'ms-search__searchForm',
                'aria-label': 'Search',
                name: 'searchForm',
                role: 'search',
                action: searchURL,
                autoComplete: 'off'
            },
            label: LabelComponent({
                isSearchFormExpanded,
                searchLabelClass: `${this.searchLabelClass} ${isSearchFormExpanded ? 'bx-hide' : 'bx-show'}`,
                searchtext,
                searchLabelArialLabel,
                searchCollapsedLabel: this.searchCollapsedLabel,
                hideSearchLabel,
                telemetryContent: this.telemetryContent
            }),
            form: FormComponent({
                ariaLabelCancel: cancelBtnAriaLabel,
                ariaLabelSearch: searchBtnAriaLabel,
                ariaLabelSubmit: submitBtnAriaLabel,
                hideSearchLabel,
                searchTextInput: this.searchTextInput,
                disableSubmitSearch,
                maxChars: this.maxChars,
                searchQsp,
                searchPlaceholder,
                query: searchText
            }),
            unbxdAutosuggestJs: unbxdAutosuggestJs,
            unbxdAutosuggestCss: unbxdAutosuggestCss,
            isSearchFormExpanded: this.state.isSearchFormExpanded
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private readonly _appendQueryParams = (route: string, query: string, qsp: string): string => {
        if (!MsDyn365.isBrowser) {
            return '';
        }

        const queryUrl = new URL(route, window.location.href);
        if (qsp && query) {
            queryUrl.searchParams.set(qsp, query);
        }

        return queryUrl.href;
    };
}

export default Search;
